import { leadImage, resolveTile, assetUrl } from "@/lib/gallery";
import { roomTiles, pct } from "@/lib/layouts";

const FALLBACK = "https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export const isDark = (hex) => {
  if (!hex) return false;
  const c = hex.replace("#", "");
  if (c.length < 6) return false;
  const r = parseInt(c.substr(0, 2), 16), g = parseInt(c.substr(2, 2), 16), b = parseInt(c.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 130;
};

const swatchKey = (p) => `${p.hex}-${p.name}`;

function CoverSlide({ slide, project }) {
  const first = project.moodboards?.[0];
  return (
    <div className="aspect-[16/9] grid grid-cols-2">
      <div className="p-10 sm:p-16 flex flex-col justify-between">
        <div className="eyebrow mb-4">{slide.studio}</div>
        <div>
          <div className="eyebrow mb-4">Concept Proposal</div>
          <h1 className="editorial-heading text-4xl sm:text-6xl mb-6">{slide.title}</h1>
          <div className="hairline my-6" />
          <div className="text-sm text-stone-600">{slide.client}</div>
          <div className="eyebrow mt-2">{slide.subtitle}</div>
        </div>
        <div className="text-xs font-mono-lab text-stone-400">Vol. 01</div>
      </div>
      <div className="overflow-hidden">
        <img src={(first && leadImage(first, project)) || FALLBACK} alt={`${slide.title} cover`} className="w-full h-full object-cover" />
      </div>
    </div>
  );
}

function PaletteSlide({ slide }) {
  return (
    <div className="aspect-[16/9] p-10 sm:p-16 flex flex-col">
      <div className="eyebrow mb-3">04</div>
      <h2 className="editorial-heading text-4xl sm:text-5xl mb-10">{slide.title}</h2>
      <div className="grid grid-cols-5 gap-4 flex-1">
        {(slide.palette || []).map((p) => (
          <div key={swatchKey(p)} className="flex flex-col justify-end p-4" style={{ background: p.hex, color: isDark(p.hex) ? "#F5F5F0" : "#1A1A18" }}>
            <div className="eyebrow" style={{ opacity: 0.75 }}>{p.hex}</div>
            <div className="text-lg font-medium mt-1">{p.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MaterialsSlide({ slide }) {
  const imgs = slide.material_images || {};
  return (
    <div className="aspect-[16/9] p-10 sm:p-16">
      <div className="eyebrow mb-3">05</div>
      <h2 className="editorial-heading text-4xl sm:text-5xl mb-8">{slide.title}</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(slide.materials || {}).map(([k, v]) => {
          const src = assetUrl(imgs[k]?.url);
          return (
            <div key={k} className="flex gap-4 items-start" data-testid={`material-${k}`}>
              {src ? <img src={src} alt={`${v} material sample`} className="w-24 h-24 lg:w-28 lg:h-28 object-cover flex-shrink-0" /> : <div className="w-0.5 self-stretch bg-black" />}
              <div>
                <div className="eyebrow mb-1">{k}</div>
                <div className="font-serif-editorial text-xl lg:text-2xl leading-snug">{v}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlanSlide({ slide }) {
  const plan = slide.plan || {};
  return (
    <div className="aspect-[16/9] grid grid-cols-12" data-testid="plan-slide">
      <div className="col-span-4 p-10 flex flex-col justify-between">
        <div>
          <div className="eyebrow mb-3">{slide.title}</div>
          <h2 className="editorial-heading text-3xl lg:text-4xl mb-4 capitalize">{slide.title?.toLowerCase()}</h2>
          <div className="text-sm text-stone-600 mb-4">{slide.body}</div>
          <ul className="space-y-1 text-xs font-mono-lab text-stone-500">{(slide.meta || []).map(m => <li key={m}>— {m}</li>)}</ul>
        </div>
        <div className="text-[10px] font-mono-lab text-stone-400 truncate">{plan.name}</div>
      </div>
      <div className="col-span-8 p-6 flex items-center justify-center bg-[#FAFAF7]">
        <img src={assetUrl(plan.url)} alt="Floor plan" className="max-w-full max-h-full object-contain" data-testid="plan-slide-image" />
      </div>
    </div>
  );
}

function DnaSlide({ slide, project }) {
  const dna = slide.dna || project.dna || {};
  return (
    <div className="aspect-[16/9] p-10 sm:p-16 grid grid-cols-2 gap-10">
      <div>
        <div className="eyebrow mb-3">03</div>
        <h2 className="editorial-heading text-4xl sm:text-5xl mb-6">{slide.title}</h2>
        <p className="font-serif-editorial text-2xl italic text-stone-700 mb-6">{dna.forms}</p>
        <div className="flex flex-wrap gap-2">
          {(dna.keywords || []).map(k => <span key={k} className="text-xs font-mono-lab uppercase tracking-wider border border-stone-300 px-2 py-1">{k}</span>)}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {(dna.palette || []).slice(0, 6).map((p) => (<div key={swatchKey(p)} style={{ background: p.hex }} />))}
      </div>
    </div>
  );
}

function RoomSlide({ slide, project }) {
  const mb = slide.moodboard || {};
  const layout = mb.layout || "hero";
  const count = mb.image_count || 3;
  const gallery = (mb.gallery || []).slice(0, count);
  const tiles = roomTiles(layout, gallery.length || 1);
  const textures = (mb.textures || []).filter(t => t.url).slice(0, 3);
  const textPos = layout === "grid" ? { left: "4%", top: "5%", width: "92%" } : layout === "editorial" ? { left: "5%", top: "36%", width: "40%" } : { left: "6%", top: "16%", width: "30%" };
  return (
    <div className="aspect-[16/9] relative overflow-hidden bg-white">
      {gallery.length ? gallery.map((item, j) => (
        <div key={`${item.type}-${item.id || item.url || j}`} className="absolute overflow-hidden bg-stone-100" style={pct(tiles[j])}>
          <img src={resolveTile(item, mb, project) || FALLBACK} alt={`${mb.room || slide.title} mood board`} className="w-full h-full object-cover" />
        </div>
      )) : <div className="absolute overflow-hidden bg-stone-100" style={pct(tiles[0])}><img src={leadImage(mb, project) || FALLBACK} alt="" className="w-full h-full object-cover" /></div>}
      <div className={`absolute ${layout === "editorial" ? "bg-[#F5F5F0] p-[2.5%] -m-[1%]" : ""} ${layout === "grid" ? "flex gap-[4%] items-start" : ""}`} style={textPos}>
        <div className={layout === "grid" ? "w-[40%]" : ""}>
          <div className="eyebrow mb-1">{slide.eyebrow || "Room"}</div>
          <h2 className="editorial-heading text-2xl lg:text-3xl mb-2">{slide.title}</h2>
        </div>
        <p className={`text-[11px] lg:text-xs text-stone-600 leading-relaxed ${layout === "grid" ? "flex-1 pt-[2%]" : "mb-3"}`}>{mb.design_intent}</p>
        {layout !== "grid" && (
          <>
            <div className="grid grid-cols-4 gap-1 mb-2">
              {(mb.palette || []).slice(0, 4).map((p) => (<div key={swatchKey(p)} className="h-4 lg:h-6" style={{ background: p.hex }} />))}
            </div>
            {textures.length ? (
              <div className="grid grid-cols-3 gap-1">
                {textures.map((t, j) => (
                  <div key={`${t.material}-${j}`}>
                    <div className="aspect-[4/3] overflow-hidden"><img src={assetUrl(t.url)} alt={`${t.material} texture`} className="w-full h-full object-cover" /></div>
                    <div className="text-[8px] lg:text-[9px] text-stone-500 leading-tight mt-0.5 truncate">{t.material}{t.finish ? ` · ${t.finish}` : ""}</div>
                  </div>
                ))}
              </div>
            ) : <div className="text-[10px] text-stone-500">{mb.materials?.primary} · {mb.materials?.secondary} · {mb.materials?.accent}</div>}
          </>
        )}
      </div>
    </div>
  );
}

function TextSlide({ slide }) {
  return (
    <div className="aspect-[16/9] p-10 sm:p-16 flex flex-col justify-center max-w-[900px]">
      <div className="eyebrow mb-3">{slide.type?.replace("_", " ")}</div>
      <h2 className="editorial-heading text-4xl sm:text-5xl mb-8">{slide.title}</h2>
      <p className="text-lg text-stone-700 leading-relaxed">{slide.body}</p>
    </div>
  );
}

const RENDERERS = { cover: CoverSlide, plan: PlanSlide, palette: PaletteSlide, materials: MaterialsSlide, dna: DnaSlide, room: RoomSlide };

export function Slide({ slide, project }) {
  const Renderer = RENDERERS[slide.type] || TextSlide;
  return <Renderer slide={slide} project={project} />;
}
